import { OrderStatusCode } from '@gooduncles/gu-app-schema';
import { Segmented, Select } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { combineLatest, map } from 'rxjs';
import { PackingSheet } from 'src/schema/schema-packing-sheet';

import { formatDate } from 'src/utils/date-util';
import { observeIssueTasks, observeOrder, observeStoreIssue } from 'src/utils/firebase/firebase-short-cut';
import { filterIssueTasksForPaperInvoice } from 'src/utils/issue-task-util';
import { aggregateBagItems, generatePackingSheets } from 'src/utils/packing-sheet-util';

import { selectPartnersUsers } from 'src/redux/slices/partners';
import { useAppSelector } from 'src/redux/store';

import useDeliveryRouteHistoryForDate from 'src/hooks/useDeliveryRouteHistoryForDate';
import useStores from 'src/hooks/useStores';
import { useTitle } from 'src/hooks/useTitle';

import classes from './PackingSheetDashboard.module.scss';

import DatePickerWithArrows from '../Common/DatePickerWithArrows/DatePickerWithArrows';
import PrintWindow from '../PrintWindow/PrintWindow';
import PackingSheetSummaryTable from './PackingSheetSummaryTable/PackingSheetSummaryTable';
import PackingSheetTable from './PackingSheetTable/PackingSheetTable';

/**
 * 패킹표
 * FIXME: issue migration ✅
 */
const PackingSheetDashboard: FC = () => {
  useTitle('패킹표');
  const { date, setDate, dateForWhere, deliveryRouteHistory } = useDeliveryRouteHistoryForDate();
  const stores = useStores();
  const partnersUsers = useAppSelector(selectPartnersUsers);
  const [packingSheets, setPackingSheets] = useState<Record<string, PackingSheet>>();
  const [packingSheetsLoading, setPackingSheetsLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState<string>();
  const [viewMode, setViewMode] = useState<'store' | 'summary'>('store');
  const activePartners = useMemo(() => Object.keys(packingSheets ?? {}), [packingSheets]);
  const packingSheet = selectedPartner ? packingSheets?.[selectedPartner] : undefined;
  const bagItems = useMemo(() => (packingSheet ? aggregateBagItems(packingSheet) : []), [packingSheet]);
  const options = useMemo(
    () =>
      [
        ...partnersUsers
          .map((partner) => ({ value: partner._id, label: `${partner.deliveryPartnerStatus.sortKey}-${partner.name}` }))
          .sort((a, b) => {
            const aNum = parseInt(a.label.split('-')[0], 10);
            const bNum = parseInt(b.label.split('-')[0], 10);
            return aNum - bNum;
          }),
        {
          value: 'undefined',
          label: '미지정',
        },
      ].filter((c) => activePartners?.includes(c?.value ?? '') ?? false),
    [activePartners, partnersUsers],
  );

  useEffect(() => {
    if (date && stores && deliveryRouteHistory) {
      const issueDate = formatDate(dateForWhere, "yyyy-MM-dd'T'00:00:00+0900");
      const acceptedOrdersObservable = observeOrder([['orderStatus', '==', OrderStatusCode.ACCEPTED]]);
      const deliveredOrdersObservable = observeOrder([
        ['orderStatus', '==', OrderStatusCode.DELIVERED],
        ['date', '==', dateForWhere],
      ]);
      const storeIssuesObservable = observeStoreIssue([['date', '==', issueDate]]).pipe(
        map((issues) =>
          issues
            .filter((issue) => issue.isDeleted !== true)
            .filter((issue) => issue.status === '입력' && ['배송', '회수', '보관'].includes(issue.category ?? '')),
        ),
      );
      const issueTasksObservable = observeIssueTasks([['targetDate', '==', dateForWhere]]).pipe(
        map((tasks) => filterIssueTasksForPaperInvoice(tasks)),
      );
      setPackingSheetsLoading(true);
      const subscription = combineLatest([
        storeIssuesObservable,
        acceptedOrdersObservable,
        deliveredOrdersObservable,
        issueTasksObservable,
      ]).subscribe(([storeIssues, acceptedOrders, deliveredOrders, issueTasks]) => {
        const packingSheets = generatePackingSheets(
          deliveryRouteHistory,
          stores,
          [...acceptedOrders, ...deliveredOrders],
          storeIssues,
          issueTasks,
        );
        setPackingSheets(packingSheets);
        setPackingSheetsLoading(false);
      });

      return () => {
        setPackingSheetsLoading(false);
        subscription.unsubscribe();
      };
    } else {
      setPackingSheets(undefined);
    }
  }, [stores, deliveryRouteHistory, date, dateForWhere]);

  useEffect(() => {
    if (options.length > 0) {
      // 기존에 선택한 파트너가 데이터 변경으로 없어진 경우
      if (!options.map((o) => o.value).includes(selectedPartner ?? '')) {
        setSelectedPartner(options[0]?.value);
      }
    } else {
      setSelectedPartner(undefined);
    }
  }, [options, selectedPartner]);

  return (
    <div className={classes.dashboardConatiner}>
      <div className={classes.dashboardToolbar}>
        <DatePickerWithArrows date={date} setDate={setDate} />
        <Select
          value={selectedPartner}
          onChange={setSelectedPartner}
          options={options}
          style={{ minWidth: 200 }}
          loading={!partnersUsers || packingSheetsLoading}
        />
        <Segmented
          value={viewMode}
          onChange={setViewMode}
          options={[
            { value: 'store', label: '매장별' },
            { value: 'summary', label: '소분 합산' },
          ]}
        />
        <p>* {"'완료'"}된 이슈는 표시되지 않습니다.</p>
      </div>
      <div className={classes.dashboardContent}>
        {!packingSheetsLoading && (
          <PrintWindow>
            {packingSheet && viewMode === 'store' && (
              <>
                <div className={classes.packingSheetHeader}>
                  {partnersUsers.find((c) => c._id === selectedPartner)?.name} / 스팟{' '}
                  {packingSheet.deliverySpots.length}개 / 매장{' '}
                  {packingSheet.deliverySpots.reduce((acc, s) => acc + s.stores.length, 0)}개
                </div>
                <PackingSheetTable packingSheet={packingSheet} />
              </>
            )}
            {packingSheet && viewMode === 'summary' && (
              <>
                <div className={classes.packingSheetHeader}>
                  {partnersUsers.find((c) => c._id === selectedPartner)?.name} / 소분 {bagItems.length}종 / 총{' '}
                  {Math.round(bagItems.reduce((acc, item) => acc + item.quantity, 0) * 100) / 100}개
                </div>
                <PackingSheetSummaryTable items={bagItems} />
              </>
            )}
          </PrintWindow>
        )}
      </div>
    </div>
  );
};

export default PackingSheetDashboard;
